/* La Pepa · Shop by mood (sections/lp-mood.liquid)
 * Chips schalten Bandfarbe, Akzentkreis, Stimmungszeile, Karussell und Kopf-Werkzeuge („View all“, Pfeile) um.
 * Tastatur im Tablist-Muster (Pfeiltasten, Pos1/Ende). Beim ersten Einblenden zeichnet sich der Unterstrich und die
 * Karten gleiten gestaffelt ein (nur wenn die Section beim Laden noch unterhalb des Bildschirms liegt).
 */
class LpMood extends HTMLElement {
  static BLOBS = [
    ['28%', '-42%', '1'],
    ['18%', '-30%', '.85'],
    ['34%', '-50%', '1.1'],
    ['22%', '-36%', '.95'],
    ['30%', '-44%', '.9'],
    ['20%', '-32%', '1.05'],
  ];

  #abort;
  #io;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
    this.band = this.querySelector('.lp-mood__band');

    this.addEventListener('click', (event) => {
      const chip = event.target.closest('.lp-mood__chip');
      if (chip && this.contains(chip)) this.select(chip);
    }, { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    // Theme-Editor: gewählte Stimmung anzeigen
    this.addEventListener('shopify:block:select', (event) => {
      const chip = event.target.closest?.('.lp-mood__chip');
      if (chip) this.select(chip);
    }, { signal });

    if (this.reduce.matches || !('IntersectionObserver' in window) || this.getBoundingClientRect().top < window.innerHeight) return;
    this.classList.add('is-armed');
    this.#io = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#io.disconnect();
      this.classList.add('is-in');
      const pane = this.querySelector('.lp-mood__pane:not([hidden])');
      if (pane) this.#replay(pane);
    }, { threshold: 0.2 });
    this.#io.observe(this.band);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#io?.disconnect();
  }

  get chips() {
    return Array.from(this.querySelectorAll('.lp-mood__chip'));
  }

  select(chip, { focus = false } = {}) {
    const key = chip.dataset.k;
    if (focus) chip.focus();
    if (chip.getAttribute('aria-selected') === 'true') return;

    const blob = LpMood.BLOBS[Number(key) % LpMood.BLOBS.length];
    const band = this.band.style;
    if (chip.dataset.color) band.setProperty('--lp-mood-band', chip.dataset.color);
    if (chip.dataset.accent) band.setProperty('--lp-mood-accent', chip.dataset.accent);
    ['--lp-mood-ax', '--lp-mood-ay', '--lp-mood-as'].forEach((name, i) => band.setProperty(name, blob[i]));

    this.chips.forEach((c) => {
      const active = c === chip;
      c.setAttribute('aria-selected', String(active));
      c.tabIndex = active ? 0 : -1;
    });
    this.querySelectorAll('.lp-mood__tools').forEach((tools) => { tools.hidden = tools.dataset.tools !== key; });
    this.querySelectorAll('.lp-mood__line').forEach((line) => {
      line.hidden = line.dataset.line !== key;
      if (!line.hidden) this.#replay(line);
    });
    this.querySelectorAll('.lp-mood__pane').forEach((pane) => {
      pane.hidden = pane.dataset.pane !== key;
      if (pane.hidden) return;
      // Karten der übrigen Stimmungen liefert die Section als <template> (DOM klein, Judge.me rechnet nur sichtbare)
      const cards = pane.querySelector('template[data-cards]');
      if (cards) cards.replaceWith(cards.content);
      const carousel = pane.querySelector('scroll-carousel');
      if (carousel) carousel.scrollLeft = 0;
      this.#replay(pane);
    });

    // Chip-Reihe (mobil wischbar) mittig auf den gewählten Chip ziehen
    const row = chip.parentElement;
    if (row.scrollWidth > row.clientWidth) {
      const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
      row.scrollTo({ left, behavior: this.reduce.matches ? 'auto' : 'smooth' });
    }
  }

  #replay(element) {
    if (this.reduce.matches) return;
    element.classList.remove('is-entering');
    void element.offsetWidth;
    element.classList.add('is-entering');
    clearTimeout(element.lpMoodTimer);
    element.lpMoodTimer = setTimeout(() => element.classList.remove('is-entering'), 1400);
  }

  #onKeydown(event) {
    const chips = this.chips;
    const index = chips.indexOf(event.target);
    if (index < 0) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = null;
    if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
    else if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = chips.length - 1;
    if (next === null) return;
    event.preventDefault();
    this.select(chips[(next + chips.length) % chips.length], { focus: true });
  }
}

if (!customElements.get('lp-mood')) customElements.define('lp-mood', LpMood);
